/**
 * 中国行政区划数据工具
 *
 * 将行政区划数据转换为 Ant Design Cascader 组件所需的格式。
 * 使用 china-division 包 (optional peer dependency) 或自定义数据源。
 *
 * **注意**: 该模块专为中国行政区划设计 (China-specific)。
 * 数据源通过 `setRegionDataSource()` 注入，不直接依赖具体数据包。
 */

// ===== 类型定义 =====

/**
 * 级联选项
 */
export interface CascaderOption {
  /** 显示文本 */
  label: string;
  /** 值 */
  value: string;
  /** 子选项 */
  children?: CascaderOption[];
}

/**
 * 省/自治区/直辖市
 */
interface ProvinceItem {
  code: string;
  name: string;
}

/**
 * 城市
 */
interface CityItem {
  code: string;
  name: string;
  provinceCode: string;
}

/**
 * 区/县
 */
interface AreaItem {
  code: string;
  name: string;
  cityCode: string;
  provinceCode: string;
}

/**
 * 原始地区数据
 */
export interface RegionDataSource {
  provinces: ProvinceItem[];
  cities: CityItem[];
  areas: AreaItem[];
}

/**
 * 地区数据配置
 */
export interface RegionConfig {
  /**
   * Cascader option 的 value 使用哪个字段
   * - 'code': 使用行政区划编码 (默认, 如 '110000')
   * - 'name': 使用名称 (如 '北京市')
   */
  valueField?: 'code' | 'name';
}

// ===== 缓存 =====

let cachedRegionData: CascaderOption[] | null = null;
let cachedCityData: CascaderOption[] | null = null;
let cachedDataSource: RegionDataSource | null = null;
let cachedValueField: 'code' | 'name' = 'code';
/** code -> name 的 Map 缓存，用于 O(1) 名称查找 */
let cachedNameMap: Map<string, string> | null = null;

/**
 * 重置缓存 (测试用)
 */
export function resetRegionCache(): void {
  cachedRegionData = null;
  cachedCityData = null;
  cachedDataSource = null;
  cachedNameMap = null;
}

// ===== 核心函数 =====

/**
 * 设置数据源
 *
 * 如果使用 china-division 包, 需要在应用初始化时调用此方法设置数据源。
 *
 * @example
 * ```ts
 * import provinces from 'china-division/dist/provinces.json';
 * import cities from 'china-division/dist/cities.json';
 * import areas from 'china-division/dist/areas.json';
 *
 * setRegionDataSource({ provinces, cities, areas });
 * ```
 */
export function setRegionDataSource(
  data: RegionDataSource,
  config?: RegionConfig,
): void {
  cachedDataSource = data;
  cachedValueField = config?.valueField ?? 'code';
  // 清除缓存，下次获取时重新构建
  cachedRegionData = null;
  cachedCityData = null;
  cachedNameMap = null;
}

/**
 * 获取省市区三级级联数据
 *
 * @returns Ant Design Cascader 格式的省市区数据
 *
 * @example
 * ```tsx
 * import { getRegionData } from '@aspect/shared/utils';
 *
 * <Cascader options={getRegionData()} placeholder="请选择地区" />
 * ```
 */
export function getRegionData(): CascaderOption[] {
  if (cachedRegionData) return cachedRegionData;
  if (!cachedDataSource) {
    return [];
  }

  const { provinces, cities, areas } = cachedDataSource;

  const vf = cachedValueField;

  // 构建区 -> 城市映射
  const areasByCity = new Map<string, CascaderOption[]>();
  for (const area of areas) {
    const list = areasByCity.get(area.cityCode) ?? [];
    list.push({ label: area.name, value: area[vf] });
    areasByCity.set(area.cityCode, list);
  }

  // 构建城市 -> 省映射
  const citiesByProvince = new Map<string, CascaderOption[]>();
  for (const city of cities) {
    const list = citiesByProvince.get(city.provinceCode) ?? [];
    list.push({
      label: city.name,
      value: city[vf],
      children: areasByCity.get(city.code),
    });
    citiesByProvince.set(city.provinceCode, list);
  }

  // 构建省级
  cachedRegionData = provinces.map((province) => ({
    label: province.name,
    value: province[vf],
    children: citiesByProvince.get(province.code),
  }));

  return cachedRegionData;
}

/**
 * 获取省市二级级联数据 (不含区/县)
 *
 * @returns Ant Design Cascader 格式的省市数据
 *
 * @example
 * ```tsx
 * <Cascader options={getCityData()} placeholder="请选择城市" />
 * ```
 */
export function getCityData(): CascaderOption[] {
  if (cachedCityData) return cachedCityData;
  if (!cachedDataSource) {
    return [];
  }

  const { provinces, cities } = cachedDataSource;

  const vf = cachedValueField;

  const citiesByProvince = new Map<string, CascaderOption[]>();
  for (const city of cities) {
    const list = citiesByProvince.get(city.provinceCode) ?? [];
    list.push({ label: city.name, value: city[vf] });
    citiesByProvince.set(city.provinceCode, list);
  }

  cachedCityData = provinces.map((province) => ({
    label: province.name,
    value: province[vf],
    children: citiesByProvince.get(province.code),
  }));

  return cachedCityData;
}

/**
 * 根据地区编码获取名称
 *
 * @param code - 行政区划编码
 * @returns 地区名称, 未找到返回 code 本身
 *
 * @example
 * ```ts
 * getRegionName('110000'); // => '北京市'
 * getRegionName('110100'); // => '北京市' (市辖区)
 * getRegionName('110101'); // => '东城区'
 * ```
 */
export function getRegionName(code: string): string {
  if (!cachedDataSource) return code;

  // 惰性构建 name 查找 Map (O(1) 查找，替代三次线性搜索)
  if (!cachedNameMap) {
    cachedNameMap = new Map();
    for (const p of cachedDataSource.provinces)
      cachedNameMap.set(p.code, p.name);
    for (const c of cachedDataSource.cities) cachedNameMap.set(c.code, c.name);
    for (const a of cachedDataSource.areas) cachedNameMap.set(a.code, a.name);
  }

  return cachedNameMap.get(code) ?? code;
}

/**
 * 根据编码数组获取完整地址文本
 *
 * @param codes - [省编码, 市编码?, 区编码?]
 * @param separator - 分隔符, 默认 ' '
 * @returns 完整地址文本
 *
 * @example
 * ```ts
 * getRegionFullText(['110000', '110100', '110101']);
 * // => '北京市 北京市 东城区'
 *
 * getRegionFullText(['110000', '110100', '110101'], '/');
 * // => '北京市/北京市/东城区'
 * ```
 */
export function getRegionFullText(codes: string[], separator = ' '): string {
  return codes.filter(Boolean).map(getRegionName).join(separator);
}
